import { useCallback, useEffect, useRef } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { getSocketConnection } from '../api/createSocketConnection';
import { startListening } from '../state/listenerMiddleware';
import { socketConnected } from '../state/app/app.slice';

const useSocket = <T>(channel: string, callback: (data: T) => void) => {
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  const handler = useCallback((data: T) => callbackRef.current(data), []);

  const subscribe = useCallback(async () => {
    const socketConnection = await getSocketConnection();

    if (socketConnection?.state !== HubConnectionState.Connected) {
      return;
    }

    socketConnection.on(channel, handler);
  }, [channel, handler]);

  const unsubscribe = useCallback(async () => {
    const socketConnection = await getSocketConnection();

    socketConnection?.off(channel, handler);
  }, [channel, handler]);

  useEffect(() => {
    subscribe();

    const unsubscribeListener = startListening({
      actionCreator: socketConnected,
      effect: subscribe,
    });

    return () => {
      unsubscribe();
      unsubscribeListener();
    };
  }, [subscribe, unsubscribe]);
};

export default useSocket;
